import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { 
  ArrowLeft, 
  Building2, 
  Globe, 
  MapPin, 
  Users,
  DollarSign,
  Briefcase,
  Edit,
  Trash2,
  Sparkles
} from "lucide-react";
import EditCompanyDialog from "@/components/EditCompanyDialog";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const CompanyDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [company, setCompany] = useState(null);
  const [loading, setLoading] = useState(true);
  const [enriching, setEnriching] = useState(false);
  const [showEdit, setShowEdit] = useState(false);

  useEffect(() => {
    fetchCompanyDetails();
  }, [id]);

  const fetchCompanyDetails = async () => {
    try {
      const response = await axios.get(`${API}/companies/${id}`, {
        withCredentials: true
      });
      setCompany(response.data);
      setLoading(false);
    } catch (error) {
      console.error("Error fetching company:", error);
      toast.error("Failed to fetch company details");
      setLoading(false);
    }
  };

  const handleEnrich = async () => {
    setEnriching(true);
    try {
      const response = await axios.post(`${API}/companies/${id}/enrich`, {}, {
        withCredentials: true
      });
      toast.success(`Enriched ${response.data.enriched_fields.length} fields!`);
      setCompany(response.data.company);
    } catch (error) {
      console.error("Error enriching company:", error);
      toast.error(error.response?.data?.detail || "Failed to enrich company");
    } finally {
      setEnriching(false);
    }
  };

  const handleDelete = async () => {
    if (!window.confirm("Are you sure you want to delete this company?")) return;
    
    try {
      await axios.delete(`${API}/companies/${id}`, {
        withCredentials: true
      });
      toast.success("Company deleted successfully!");
      navigate("/companies");
    } catch (error) {
      toast.error("Failed to delete company");
    }
  };

  if (loading || !company) {
    return <div className="text-center py-12">Loading...</div>;
  }

  return (
    <div className="space-y-6 fade-in">
      <Button variant="outline" onClick={() => navigate('/companies')}>
        <ArrowLeft className="w-4 h-4 mr-2" />
        Back to Companies
      </Button>

      <div className="glass-card rounded-xl p-8">
        <div className="flex items-start justify-between">
          <div className="flex items-start space-x-4">
            <div className="w-16 h-16 bg-gradient-to-br from-indigo-600 to-blue-600 rounded-xl flex items-center justify-center text-white text-2xl font-bold">
              {company.name.charAt(0).toUpperCase()}
            </div>
            <div>
              <h1 className="text-3xl font-bold text-slate-900">{company.name}</h1>
              {company.description && <p className="text-slate-600 mt-2">{company.description}</p>}
            </div>
          </div>
          <div className="flex space-x-2">
            <Button 
              variant="outline" 
              onClick={handleEnrich}
              disabled={enriching}
              data-testid="enrich-company-btn"
            >
              <Sparkles className={`w-4 h-4 mr-2 ${enriching ? 'animate-spin' : ''}`} />
              {enriching ? "Enriching..." : "Enrich with AI"}
            </Button>
            <Button variant="outline" onClick={() => setShowEdit(true)} data-testid="edit-company-btn">
              <Edit className="w-4 h-4 mr-2" />
              Edit
            </Button>
            <Button variant="outline" onClick={handleDelete} className="text-red-600 hover:text-red-700">
              <Trash2 className="w-4 h-4 mr-2" />
              Delete
            </Button>
          </div>
        </div>
      </div>

      {/* AI Enriched Data Section */}
      {(company.overview || company.employee_count || company.headquarters || company.revenue_range || company.summary) && (
        <Card className="glass-card border-2 border-indigo-100">
          <CardHeader className="bg-gradient-to-r from-indigo-50 to-blue-50">
            <div className="flex items-center justify-between">
              <CardTitle className="flex items-center gap-2">
                <Sparkles className="w-5 h-5 text-indigo-600" />
                AI-Enriched Company Intelligence
              </CardTitle>
            </div>
          </CardHeader>
          <CardContent className="space-y-6 pt-6">
            {company.overview && (
              <div>
                <h3 className="text-sm font-semibold text-slate-700 mb-2">Company Overview</h3>
                <p className="text-slate-600 leading-relaxed">{company.overview}</p>
              </div>
            )}
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {company.employee_count && (
                <div className="bg-slate-50 p-4 rounded-lg">
                  <div className="flex items-center gap-2 mb-1">
                    <Users className="w-4 h-4 text-indigo-600" />
                    <p className="text-xs font-medium text-slate-500">Employee Count</p>
                  </div>
                  <p className="text-slate-900 font-semibold">{company.employee_count}</p>
                </div>
              )}
              
              {company.headquarters && (
                <div className="bg-slate-50 p-4 rounded-lg">
                  <div className="flex items-center gap-2 mb-1">
                    <MapPin className="w-4 h-4 text-indigo-600" />
                    <p className="text-xs font-medium text-slate-500">Headquarters</p>
                  </div>
                  <p className="text-slate-900 font-semibold">{company.headquarters}</p>
                </div>
              )}
              
              {company.revenue_range && (
                <div className="bg-slate-50 p-4 rounded-lg">
                  <div className="flex items-center gap-2 mb-1">
                    <DollarSign className="w-4 h-4 text-indigo-600" />
                    <p className="text-xs font-medium text-slate-500">Revenue Range</p>
                  </div>
                  <p className="text-slate-900 font-semibold">{company.revenue_range}</p>
                </div>
              )}
              
              {company.industry && (
                <div className="bg-slate-50 p-4 rounded-lg">
                  <div className="flex items-center gap-2 mb-1">
                    <Briefcase className="w-4 h-4 text-indigo-600" />
                    <p className="text-xs font-medium text-slate-500">Industry</p>
                  </div>
                  <p className="text-slate-900 font-semibold">{company.industry}</p>
                </div>
              )}
            </div>
            
            {company.summary && (
              <div className="bg-indigo-50 p-4 rounded-lg border border-indigo-100">
                <h3 className="text-sm font-semibold text-indigo-900 mb-2">Summary</h3>
                <p className="text-indigo-800 text-sm leading-relaxed">{company.summary}</p>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <Card className="glass-card">
          <CardHeader>
            <CardTitle>Basic Information</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {company.website && (
              <div className="flex items-center space-x-3">
                <Globe className="w-5 h-5 text-slate-400" />
                <a href={company.website} target="_blank" rel="noopener noreferrer" className="text-indigo-600 hover:underline">
                  {company.website}
                </a>
              </div>
            )}
            {company.location && (
              <div className="flex items-center space-x-3">
                <MapPin className="w-5 h-5 text-slate-400" />
                <span>{company.location}</span>
              </div>
            )}
            {company.size && (
              <div className="flex items-center space-x-3">
                <Users className="w-5 h-5 text-slate-400" />
                <span>{company.size} employees</span>
              </div>
            )}
            {company.revenue && (
              <div className="flex items-center space-x-3">
                <DollarSign className="w-5 h-5 text-slate-400" />
                <span>{company.revenue}</span>
              </div>
            )}
            {!company.website && !company.location && !company.size && !company.revenue && (
              <p className="text-slate-500 text-sm">No basic information available. Click "Enrich with AI" to add details.</p>
            )}
          </CardContent>
        </Card>

        <Card className="glass-card">
          <CardHeader>
            <CardTitle>Related Leads ({company.leads?.length || 0})</CardTitle>
          </CardHeader>
          <CardContent>
            {company.leads && company.leads.length > 0 ? (
              <div className="space-y-3">
                {company.leads.map((lead) => (
                  <div key={lead.id} className="p-3 bg-slate-50 rounded-lg hover:bg-slate-100 cursor-pointer" onClick={() => navigate(`/leads/${lead.id}`)}>
                    <p className="font-semibold text-slate-900">{lead.raw_data?.name}</p>
                    <p className="text-sm text-slate-600">{lead.raw_data?.title}</p>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-slate-500">No leads associated with this company</p>
            )}
          </CardContent>
        </Card>
      </div>

      {showEdit && <EditCompanyDialog company={company} open={showEdit} onClose={() => setShowEdit(false)} onUpdated={fetchCompanyDetails} />}
    </div>
  );
};

export default CompanyDetails;
